import React from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BookmarkBorderRoundedIcon from "@mui/icons-material/BookmarkBorderRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import SkipPreviousRoundedIcon from "@mui/icons-material/SkipPreviousRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import Hls from "hls.js";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useSelector } from "react-redux";

import type { ContentCardData } from "../../@types/portalAluno";
import ContentDetailsCarousel from "../../Components/ContentDetailsCarousel/ContentDetailsCarousel";
import ContentProgressPanel from "../../Components/ContentProgressPanel/ContentProgressPanel";
import EmbeddedContentViewer from "../../Components/EmbeddedContentViewer/EmbeddedContentViewer";
import type { FeedbackValue } from "../../Components/FeedbackOverlay/FeedbackOverlay";
import MediaLoadingIndicator from "../../Components/MediaLoadingIndicator/MediaLoadingIndicator";
import PdfModuleViewer from "../../Components/PdfModuleViewer/PdfModuleViewer";
import UserAvatarIcon from "../../Components/UserAvatarIcon/UserAvatarIcon";
import type {
  ContentDetailsTab,
  ContentProgressItem,
  ContentProgressSectionKind,
  ContentReview,
} from "../../@types/portalAluno";
import { isPortalContentKind } from "../../utils/portalAluno/contentKinds";
import {
  buildAppContentUrl,
  buildContentShareMessage,
  buildSharedContentUrl,
  getSharedContentSelection,
  getShareSubjectLabel,
  isSharedContentAccess,
  isSingleDeeplinkUrl,
  SHARED_CONTENT_ITEM_PARAM,
} from "../../utils/portalAluno/sharedContentLink";
import type { PortalContentKind } from "../../@types/portalAluno";
import scrollPageToTop from "../../services/scrollPageToTop";
import scrollPageToElement from "../../services/scrollPageToElement";
import { ApplicationState } from "../../store";
import ContentDetailsPageSkeleton from "./ContentDetails/ContentDetailsPageSkeleton";
import ContentModuleViewerSkeleton from "./ContentDetails/ContentModuleViewerSkeleton";
import useContentDetails from "../../hooks/PortalAluno/useContentDetails";
import "./content-details.css";
import "./style.css";

type DetailsTab = ContentDetailsTab;

interface VideoControlRangeProps {
  className: string;
  label: string;
  max: number;
  value: number;
  valueText: string;
  onChange: (value: number) => void;
  step?: number;
}

interface ContentDetailsLocationState {
  content?: ContentCardData;
  contentType?: PortalContentKind;
  from?: string;
  fromKey?: string;
  initiallySaved?: boolean;
  initialTab?: DetailsTab;
}

const videoDurationSeconds = 3 * 60 + 53;
const playbackRates = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

const getPdfFileName = (sourceUrl: string | undefined, title: string) => {
  const fileName = sourceUrl?.split(/[?#]/)[0].split("/").pop();
  return fileName || `${title}.pdf`;
};

const ensurePdfData = (item: ContentProgressItem): ContentProgressItem => ({
  ...item,
  pdf: item.pdf ?? {
    fileName: getPdfFileName(item.sourceUrl, item.title),
    sourceUrl: item.sourceUrl,
    totalPages: 1,
  },
});

const requiresExplicitCompletion = (item: ContentProgressItem) =>
  Boolean(item.externalUrl) ||
  item.contentType === "external" ||
  item.contentType === "scorm";

const formatVideoTime = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(
    remainingSeconds
  ).padStart(2, "0")}`;
};

const VideoControlRange = ({
  className,
  label,
  max,
  value,
  valueText,
  onChange,
  step = 1,
}: VideoControlRangeProps) => {
  const percentage = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const position = `${percentage}%`;

  return (
    <div className={`content-details-video-controls__slider ${className}`}>
      <span className="content-details-video-controls__slider-track" aria-hidden="true">
        <span
          className="content-details-video-controls__slider-fill"
          style={{ width: position }}
        />
      </span>
      <span
        className="content-details-video-controls__slider-thumb"
        aria-hidden="true"
        style={{ left: position }}
      />
      <input
        className="content-details-video-controls__range"
        type="range"
        min="0"
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        aria-label={label}
        aria-valuetext={valueText}
      />
    </div>
  );
};

const feedbackSubjectLabels: Record<PortalContentKind, string> = {
  conteudos: "do conteúdo",
  "seguranca-do-trabalho": "da campanha",
  "treinamentos-de-1-ponto": "do treinamento",
  comunicados: "do comunicado",
  cursos: "do curso",
  trilhas: "da trilha",
  destaques: "do destaque",
  "biblioteca-multimidia": "da multimídia",
  "biblioteca-documentos": "do documento",
  "biblioteca-games": "do game",
};

const copyShareLink = async (shareUrl: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(shareUrl);
    return;
  }

  const temporaryInput = document.createElement("textarea");
  temporaryInput.value = shareUrl;
  temporaryInput.setAttribute("readonly", "");
  temporaryInput.style.position = "fixed";
  temporaryInput.style.opacity = "0";
  document.body.appendChild(temporaryInput);
  temporaryInput.select();
  const didCopy = document.execCommand("copy");
  document.body.removeChild(temporaryInput);

  if (!didCopy) throw new Error("Não foi possível copiar o link.");
};

const ContentDetailsPage = () => {
  const { contentId = "", contentType } = useParams<{
    contentId: string;
    contentType?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUserName = useSelector(
    (state: ApplicationState) => state.auth.data?.nmUsuario
  );
  const currentUserPhotoUrl = useSelector(
    (state: ApplicationState) => state.auth.data?.imgUsuario
  );
  const navigationState = location.state as ContentDetailsLocationState | null;
  const originRoute = navigationState?.from;
  const originLocationKey = navigationState?.fromKey;
  const initiallySaved = Boolean(navigationState?.initiallySaved);
  const shouldAutoPlayOnEntry = navigationState?.from === "/app";
  const isSharedAccess = isSharedContentAccess(location.search);
  const requestedItemParam = new URLSearchParams(location.search).get(
    SHARED_CONTENT_ITEM_PARAM
  );
  const { itemId: requestedItemId, sectionId: requestedSectionId } =
    getSharedContentSelection(location.search);
  const hasRequestedItem = requestedItemParam !== null;
  const numericContentId = Number(contentId);
  const sourceTypeCandidate =
    contentType ??
    navigationState?.contentType ??
    navigationState?.content?.sourceType ??
    "cursos";
  const sourceType = isPortalContentKind(sourceTypeCandidate)
    ? sourceTypeCandidate
    : undefined;
  const contentDetailsQuery = useContentDetails(
    Number.isInteger(numericContentId) && numericContentId > 0
      ? numericContentId
      : undefined,
    sourceType
  );
  const details = contentDetailsQuery.data;
  const primaryContent = details?.primaryContent;
  const markHistory = contentDetailsQuery.markHistory;
  const loadModule = contentDetailsQuery.loadModule;
  const requestedTab = navigationState?.initialTab;
  const navigateBack = React.useCallback(
    (fallbackRoute: string) =>
      navigate(originRoute ?? fallbackRoute, {
        state: originLocationKey
          ? { restoreScrollFromKey: originLocationKey }
          : undefined,
      }),
    [navigate, originLocationKey, originRoute]
  );
  const [activeTab, setActiveTab] = React.useState<DetailsTab>(
    requestedTab ?? "about"
  );
  const [isLiked, setIsLiked] = React.useState(false);
  const [isSaved, setIsSaved] = React.useState(
    initiallySaved
  );
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [hasPlaybackStarted, setHasPlaybackStarted] = React.useState(false);
  const [videoTime, setVideoTime] = React.useState(0);
  const [videoDuration, setVideoDuration] = React.useState(videoDurationSeconds);
  const [videoVolume, setVideoVolume] = React.useState(65);
  const [isVideoMuted, setIsVideoMuted] = React.useState(false);
  const [playbackRate, setPlaybackRate] = React.useState(1);
  const [isPlaybackSettingsOpen, setIsPlaybackSettingsOpen] =
    React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isTypeCarouselOpen, setIsTypeCarouselOpen] = React.useState(false);
  const [actionStatus, setActionStatus] = React.useState("");
  const [isToastLeaving, setIsToastLeaving] = React.useState(false);
  const [selectedPdfModule, setSelectedPdfModule] =
    React.useState<ContentProgressItem | null>(null);
  const [selectedMediaItem, setSelectedMediaItem] =
    React.useState<ContentProgressItem | null>(null);
  const [selectedEmbeddedModule, setSelectedEmbeddedModule] =
    React.useState<ContentProgressItem | null>(null);
  const [isModuleLoading, setIsModuleLoading] = React.useState(false);
  const [hasSharedItemError, setHasSharedItemError] = React.useState(false);
  const [isVideoLoading, setIsVideoLoading] = React.useState(false);
  const [shouldAutoPlayVideo, setShouldAutoPlayVideo] = React.useState(false);
  const [areVideoControlsVisible, setAreVideoControlsVisible] =
    React.useState(true);
  const [activeProgressItemId, setActiveProgressItemId] = React.useState("");
  const [completedProgressItemIds, setCompletedProgressItemIds] =
    React.useState<ReadonlySet<string>>(() => new Set());
  const [userReview, setUserReview] = React.useState<ContentReview | null>(
    null
  );
  const coverRef = React.useRef<HTMLDivElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const settingsButtonRef = React.useRef<HTMLButtonElement>(null);
  const viewedPrimaryContentIdsRef = React.useRef(new Set<number>());
  const autoOpenedModuleKeyRef = React.useRef("");
  const initializedPrimaryContentKeyRef = React.useRef("");
  const syncedActionStateKeyRef = React.useRef("");
  const moduleRequestIdRef = React.useRef(0);
  const completionRequestsRef = React.useRef(new Set<number>());
  const videoControlsTimerRef = React.useRef<number>();
  const cancelModuleScrollRef = React.useRef<(() => void) | null>(null);
  const tabsRef = React.useRef<HTMLDivElement>(null);
  const hasScrolledToRequestedTabRef = React.useRef(false);

  React.useEffect(() => () => cancelModuleScrollRef.current?.(), []);

  // A entrada pode apontar para uma aba específica (ex.: o clique na nota do
  // card abre os feedbacks): levamos a leitura até as abas assim que o
  // conteúdo renderiza, uma única vez.
  React.useEffect(() => {
    if (!requestedTab || hasScrolledToRequestedTabRef.current) return;

    const tabs = tabsRef.current;

    if (!tabs) return;

    hasScrolledToRequestedTabRef.current = true;

    if (typeof window.requestAnimationFrame !== "function") {
      scrollPageToElement(tabs);
      return;
    }

    const frame = window.requestAnimationFrame(() => scrollPageToElement(tabs));

    return () => window.cancelAnimationFrame(frame);
  }, [details, requestedTab]);

  const displayContentItem = React.useCallback((item: ContentProgressItem) => {
    if (item.contentType === "pdf" && !item.externalUrl) {
      setSelectedPdfModule(ensurePdfData(item));
      setSelectedMediaItem(null);
      setSelectedEmbeddedModule(null);
      return;
    }

    if (item.contentType !== "video") {
      setSelectedPdfModule(null);
      setSelectedMediaItem(null);
      setSelectedEmbeddedModule(item);
      return;
    }

    setSelectedPdfModule(null);
    setSelectedEmbeddedModule(null);
    setSelectedMediaItem(item);
  }, []);

  const completeContentItem = React.useCallback(
    async (item: ContentProgressItem, progressItemId = item.id) => {
      const entityId = item.entityId;
      if (!entityId) {
        setActionStatus("Não foi possível registrar este conteúdo como concluído.");
        return false;
      }

      if (
        item.completed ||
        viewedPrimaryContentIdsRef.current.has(entityId)
      ) {
        setCompletedProgressItemIds((current) =>
          new Set(current).add(progressItemId)
        );
        return true;
      }

      if (completionRequestsRef.current.has(entityId)) return false;
      completionRequestsRef.current.add(entityId);

      try {
        await markHistory(entityId);
        viewedPrimaryContentIdsRef.current.add(entityId);
        setCompletedProgressItemIds((current) =>
          new Set(current).add(progressItemId)
        );
        setActionStatus(`${item.title} concluído.`);
        return true;
      } catch {
        setActionStatus(
          "Não foi possível atualizar o progresso. Tente novamente."
        );
        return false;
      } finally {
        completionRequestsRef.current.delete(entityId);
      }
    },
    [markHistory]
  );

  React.useEffect(() => {
    setActiveTab(requestedTab ?? "about");
    setIsLiked(false);
    setIsSaved(initiallySaved);
    setIsPlaying(false);
    setHasPlaybackStarted(false);
    setVideoTime(0);
    setVideoDuration(videoDurationSeconds);
    setVideoVolume(65);
    setIsVideoMuted(false);
    setPlaybackRate(1);
    setIsPlaybackSettingsOpen(false);
    setIsTypeCarouselOpen(false);
    setActionStatus("");
    setIsToastLeaving(false);
    setSelectedPdfModule(null);
    setSelectedMediaItem(null);
    setSelectedEmbeddedModule(null);
    setIsModuleLoading(false);
    setHasSharedItemError(false);
    setIsVideoLoading(false);
    setShouldAutoPlayVideo(false);
    setAreVideoControlsVisible(true);
    setActiveProgressItemId("");
    setCompletedProgressItemIds(new Set());
    setUserReview(null);
    viewedPrimaryContentIdsRef.current.clear();
    autoOpenedModuleKeyRef.current = "";
    initializedPrimaryContentKeyRef.current = "";
    syncedActionStateKeyRef.current = "";
    moduleRequestIdRef.current += 1;
    completionRequestsRef.current.clear();
  }, [
    contentId,
    initiallySaved,
    requestedTab,
    sourceType,
    requestedItemParam,
    requestedSectionId,
  ]);

  React.useEffect(() => {
    if (!details) return;

    // A revalidacao do cache devolve um objeto novo a cada toggle; reaplicar o
    // valor do servidor aqui desfaria a curtida/favorito que o usuario acabou
    // de marcar. Por isso so sincronizamos na primeira carga de cada conteudo.
    const actionStateKey = `${details.sourceType}:${details.id}`;
    if (syncedActionStateKeyRef.current !== actionStateKey) {
      syncedActionStateKeyRef.current = actionStateKey;
      setIsLiked(details.isLiked);
      setIsSaved(details.isSaved);
    }

    setUserReview(
      details.userRating
        ? {
            author: currentUserName?.trim() || "Você",
            authorPhotoUrl: currentUserPhotoUrl,
            comment: details.userComment ?? "",
            id: details.id * -10,
            rating: details.userRating,
            relativeDate: "Seu feedback",
            showStars: true,
          }
        : null
    );

    if (
      primaryContent &&
      (!hasRequestedItem || requestedItemId === primaryContent.entityId)
    ) {
      const primaryContentKey = `${details.sourceType}:${details.id}:${primaryContent.id}`;
      if (initializedPrimaryContentKeyRef.current !== primaryContentKey) {
        initializedPrimaryContentKeyRef.current = primaryContentKey;
        displayContentItem(primaryContent);

        if (
          shouldAutoPlayOnEntry &&
          primaryContent.contentType === "video" &&
          primaryContent.sourceUrl
        ) {
          setHasPlaybackStarted(true);
          setShouldAutoPlayVideo(true);
        }
      }
    }
  }, [
    currentUserName,
    currentUserPhotoUrl,
    details,
    displayContentItem,
    hasRequestedItem,
    primaryContent,
    requestedItemId,
    requestedItemParam,
    requestedSectionId,
    shouldAutoPlayOnEntry,
  ]);

  const activeMediaItem = selectedMediaItem ?? details?.primaryContent;
  const displayedContentItem =
    selectedPdfModule ?? selectedEmbeddedModule ?? selectedMediaItem;
  const isVideoContent = activeMediaItem?.contentType === "video";
  const videoSource = isVideoContent ? activeMediaItem.sourceUrl ?? "" : "";
  const isHlsSource = /\.m3u8(?:[?#].*)?$/i.test(videoSource);
  const hasDisplayedContent = Boolean(displayedContentItem);

  React.useEffect(() => {
    setIsVideoLoading(Boolean(videoSource));
  }, [videoSource]);

  React.useEffect(() => {
    if (
      !details ||
      details.id !== numericContentId ||
      details.sourceType !== sourceType
    ) {
      return;
    }
    if (
      details.entityType === "conteudo" &&
      requestedItemId &&
      requestedItemId !== details.id
    ) {
      navigate(
        `/app/conteudo/detalhes/conteudos/${requestedItemId}${location.search}`,
        { replace: true, state: navigationState }
      );
      return;
    }
    if (
      hasRequestedItem &&
      requestedItemId &&
      requestedItemId === primaryContent?.entityId
    ) {
      return;
    }
    if (
      !hasRequestedItem &&
      (details.entityType === "conteudo" || primaryContent)
    ) {
      return;
    }

    const contentSections = details.progress.sections.filter(
      (section) => section.kind !== "evaluation"
    );
    const matchingSections = contentSections.filter((section) =>
      section.items.some((item) => item.entityId === requestedItemId)
    );
    const initialSection = hasRequestedItem
      ? matchingSections.find((section) => section.id === requestedSectionId) ?? matchingSections[0]
      : contentSections.find((section) => section.items.length > 0);
    const initialItem = hasRequestedItem
      ? initialSection?.items.find((item) => item.entityId === requestedItemId)
      : initialSection?.items[0];
    const initialEntityId = hasRequestedItem ? requestedItemId : initialItem?.entityId;
    const requestKey = `${details.sourceType}:${details.id}:${requestedItemParam ?? "first"}:${requestedSectionId ?? ""}`;
    if (autoOpenedModuleKeyRef.current === requestKey) return;
    if (hasRequestedItem && !requestedItemId) {
      autoOpenedModuleKeyRef.current = requestKey;
      setHasSharedItemError(true);
      return;
    }
    if (!initialEntityId) return;

    autoOpenedModuleKeyRef.current = requestKey;
    const requestId = ++moduleRequestIdRef.current;

    setIsModuleLoading(true);
    loadModule(initialEntityId)
      .then((module) => {
        if (
          autoOpenedModuleKeyRef.current !== requestKey ||
          moduleRequestIdRef.current !== requestId
        ) {
          return;
        }

        displayContentItem(module);
        setHasSharedItemError(false);
        setActiveProgressItemId(initialItem?.id ?? module.id);
        setActiveTab(requestedTab ?? "about");
        setActionStatus(`${module.title} carregado.`);

        if (
          shouldAutoPlayOnEntry &&
          module.contentType === "video" &&
          module.sourceUrl
        ) {
          setHasPlaybackStarted(true);
          setShouldAutoPlayVideo(true);
        }

        if (
          module.contentType !== "video" &&
          !requiresExplicitCompletion(module) &&
          module.entityId
        ) {
          void completeContentItem(module, initialItem?.id ?? module.id);
        }
      })
      .catch(() => {
        if (
          autoOpenedModuleKeyRef.current !== requestKey ||
          moduleRequestIdRef.current !== requestId
        ) {
          return;
        }
        if (hasRequestedItem) setHasSharedItemError(true);
        setActionStatus(
          hasRequestedItem
            ? "Não foi possível abrir o item compartilhado. Selecione um conteúdo para tentar novamente."
            : "Não foi possível abrir o primeiro módulo. Selecione um conteúdo para tentar novamente."
        );
      })
      .finally(() => {
        if (moduleRequestIdRef.current === requestId) {
          setIsModuleLoading(false);
        }
      });
  }, [
    details,
    completeContentItem,
    displayContentItem,
    loadModule,
    hasRequestedItem,
    location.search,
    navigate,
    navigationState,
    numericContentId,
    primaryContent,
    requestedItemId,
    requestedItemParam,
    requestedSectionId,
    requestedTab,
    shouldAutoPlayOnEntry,
    sourceType,
  ]);

  React.useEffect(() => {
    const entityId = primaryContent?.entityId;
    if (
      !entityId ||
      (hasRequestedItem && requestedItemId !== entityId) ||
      primaryContent.contentType === "video" ||
      requiresExplicitCompletion(primaryContent) ||
      viewedPrimaryContentIdsRef.current.has(entityId)
    ) {
      return;
    }

    viewedPrimaryContentIdsRef.current.add(entityId);
    markHistory(entityId).catch(() => {
      viewedPrimaryContentIdsRef.current.delete(entityId);
    });
  }, [hasRequestedItem, markHistory, primaryContent, requestedItemId]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSource || !isHlsSource) return;
    if (video.canPlayType("application/vnd.apple.mpegurl")) return;

    if (!Hls.isSupported()) {
      setActionStatus("Este navegador não oferece suporte ao formato HLS.");
      return;
    }

    const hls = new Hls();
    hls.loadSource(videoSource);
    hls.attachMedia(video);
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;

      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        hls.startLoad();
        return;
      }
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        hls.recoverMediaError();
        return;
      }

      setIsPlaying(false);
      setActionStatus("Não foi possível reproduzir este stream de vídeo.");
    });

    return () => hls.destroy();
  }, [isHlsSource, videoSource]);

  React.useEffect(() => {
    if (!shouldAutoPlayVideo || !videoSource) return;

    const video = videoRef.current;
    if (!video) return;

    let isCancelled = false;
    let isAttemptingPlayback = false;

    const finishPlayback = () => {
      if (isCancelled) return;
      setShouldAutoPlayVideo(false);
      setIsVideoLoading(false);
      setIsPlaying(true);
    };

    const attemptPlayback = async () => {
      if (isCancelled || isAttemptingPlayback) return;
      isAttemptingPlayback = true;
      setHasPlaybackStarted(true);

      try {
        await video.play();
        finishPlayback();
        return;
      } catch {
        if (isCancelled) return;
      }
      video.muted = true;
      setIsVideoMuted(true);

      try {
        await video.play();
        finishPlayback();
      } catch {
        if (isCancelled) return;
        isAttemptingPlayback = false;

        if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) return;

        setShouldAutoPlayVideo(false);
        setIsPlaying(false);
        setActionStatus("O navegador bloqueou o início automático do vídeo.");
      }
    };

    const handleCanPlay = () => {
      isAttemptingPlayback = false;
      void attemptPlayback();
    };

    video.addEventListener("canplay", handleCanPlay);
    void attemptPlayback();

    return () => {
      isCancelled = true;
      video.removeEventListener("canplay", handleCanPlay);
    };
  }, [shouldAutoPlayVideo, videoSource]);

  const showVideoControls = React.useCallback(() => {
    if (videoControlsTimerRef.current !== undefined) {
      window.clearTimeout(videoControlsTimerRef.current);
    }

    setAreVideoControlsVisible(true);
    if (!isPlaying || isPlaybackSettingsOpen) return;

    videoControlsTimerRef.current = window.setTimeout(() => {
      setAreVideoControlsVisible(false);
      videoControlsTimerRef.current = undefined;
    }, 3000);
  }, [isPlaybackSettingsOpen, isPlaying]);

  React.useEffect(() => {
    if (!hasPlaybackStarted || !isPlaying || isPlaybackSettingsOpen) {
      if (videoControlsTimerRef.current !== undefined) {
        window.clearTimeout(videoControlsTimerRef.current);
        videoControlsTimerRef.current = undefined;
      }
      setAreVideoControlsVisible(true);
      return;
    }

    showVideoControls();
    return () => {
      if (videoControlsTimerRef.current !== undefined) {
        window.clearTimeout(videoControlsTimerRef.current);
        videoControlsTimerRef.current = undefined;
      }
    };
  }, [hasPlaybackStarted, isPlaybackSettingsOpen, isPlaying, showVideoControls]);

  React.useEffect(() => {
    if (!actionStatus) return;

    setIsToastLeaving(false);
    const statusTimer = window.setTimeout(() => setIsToastLeaving(true), 3260);
    return () => window.clearTimeout(statusTimer);
  }, [actionStatus]);

  React.useEffect(() => {
    if (!isToastLeaving) return;

    const exitTimer = window.setTimeout(() => {
      setActionStatus("");
      setIsToastLeaving(false);
    }, 240);

    return () => window.clearTimeout(exitTimer);
  }, [isToastLeaving]);

  React.useEffect(() => {
    if (!isPlaying || !hasPlaybackStarted || videoSource) return;

    const playbackTimer = window.setInterval(() => {
      setVideoTime((currentTime) => {
        const nextTime = currentTime + playbackRate / 4;

        if (nextTime >= videoDuration) {
          window.clearInterval(playbackTimer);
          setIsPlaying(false);
          return videoDuration;
        }

        return nextTime;
      });
    }, 250);

    return () => window.clearInterval(playbackTimer);
  }, [hasPlaybackStarted, isPlaying, playbackRate, videoDuration, videoSource]);

  React.useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === coverRef.current);
    };

    document.addEventListener("fullscreenchange", syncFullscreenState);
    return () =>
      document.removeEventListener("fullscreenchange", syncFullscreenState);
  }, []);

  if (!sourceType || !Number.isInteger(numericContentId) || numericContentId <= 0) {
    return (
      <section className="content-page content-details-page">
        <div className="content-details-page__error" role="alert">
          <h1>Conteúdo inválido</h1>
          <p>Não foi possível identificar o conteúdo solicitado.</p>
          <button type="button" onClick={() => navigate("/app")}>Voltar para a Home</button>
        </div>
      </section>
    );
  }

  if (
    contentDetailsQuery.isLoading ||
    (!contentDetailsQuery.isError &&
      (!details ||
        details.id !== numericContentId ||
        details.sourceType !== sourceType))
  ) {
    return <ContentDetailsPageSkeleton />;
  }

  if ((contentDetailsQuery.isError || !details) && isSharedAccess) {
    return (
      <section className="content-page content-details-page">
        <div className="content-details-page__error" role="alert">
          <h1>Conteúdo indisponível</h1>
          <p>
            O conteúdo compartilhado com você não está disponível para o seu
            perfil ou foi removido.
          </p>
          <div>
            <button type="button" onClick={() => contentDetailsQuery.refetch()}>
              Tentar novamente
            </button>
            <button type="button" onClick={() => navigate("/app")}>
              Voltar para a Home
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (contentDetailsQuery.isError || !details) {
    return (
      <section className="content-page content-details-page">
        <div className="content-details-page__error" role="alert">
          <h1>Não foi possível carregar os detalhes</h1>
          <p>Confira sua conexão e tente novamente.</p>
          <div>
            <button type="button" onClick={() => contentDetailsQuery.refetch()}>
              Tentar novamente
            </button>
            <button type="button" onClick={() => navigateBack("/app")}>
              Voltar
            </button>
          </div>
        </div>
      </section>
    );
  }

  const relatedCards = details.relatedCards;
  const progressContentItems = details.progress.sections
    .filter((section) => section.kind !== "evaluation")
    .flatMap((section) => section.items);
  const isEvaluationUnlocked =
    details.isCompleted ||
    (progressContentItems.length > 0 &&
      progressContentItems.every(
        (item) =>
          item.completed || completedProgressItemIds.has(item.id)
      ));

  const handleLike = async () => {
    const nextLikedState = !isLiked;
    setIsLiked(nextLikedState);
    setActionStatus(nextLikedState ? "Conteúdo curtido." : "Curtida removida.");

    try {
      await contentDetailsQuery.toggleLike({
        contentId: details.id,
        type: details.actionType,
      });
    } catch {
      setIsLiked(!nextLikedState);
      setActionStatus("Não foi possível atualizar a curtida. Tente novamente.");
    }
  };

  const handleSave = async () => {
    const nextSavedState = !isSaved;
    setIsSaved(nextSavedState);
    setActionStatus(
      nextSavedState
        ? "Conteúdo salvo nos favoritos."
        : "Conteúdo removido dos favoritos."
    );

    try {
      await contentDetailsQuery.toggleFavorite({
        contentId: details.id,
        type: details.actionType,
      });
    } catch {
      setIsSaved(!nextSavedState);
      setActionStatus("Não foi possível atualizar os favoritos. Tente novamente.");
    }
  };

  const handleHelp = () => {
    setActionStatus(
      `Ajuda aberta para ${details.moduleTitle.toLocaleLowerCase("pt-BR")}.`
    );
  };

  const handleShare = async () => {
    if (!displayedContentItem?.entityId || hasSharedItemError) return;
    const selectedSection = details.progress.sections.find(
      (section) => section.items.some((item) => item.id === activeProgressItemId)
    );
    const appUrl = buildAppContentUrl(
      details.sourceType,
      details.id,
      details.shareUrl
    );
    if (!appUrl) return;
    const isSingleLink = isSingleDeeplinkUrl(appUrl);
    const shareUrl = isSingleLink
      ? undefined
      : buildSharedContentUrl(
          `/app/conteudo/detalhes/${details.sourceType}/${details.id}`,
          {
            itemId: displayedContentItem.entityId,
            sectionId:
              selectedSection?.kind === "course"
                ? selectedSection.id
                : undefined,
          }
        );
    const subjectLabel = getShareSubjectLabel(details.sourceType);
    const message = buildContentShareMessage(
      currentUserName || "",
      subjectLabel,
      details.title,
      appUrl,
      shareUrl
    );
    const shareData = {
      title: details.title,
      text: message,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setActionStatus("Conteúdo compartilhado com sucesso.");
        return;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        setActionStatus("Compartilhamento cancelado.");
        return;
      }
    }

    try {
      await copyShareLink(message);
      setActionStatus(
        isSingleLink
          ? "Mensagem com o link copiada para compartilhar."
          : "Mensagem com os links copiada para compartilhar."
      );
    } catch {
      setActionStatus("Não foi possível compartilhar este conteúdo.");
    }
  };

  const handleFeedbackSubmit = async (feedback: FeedbackValue) => {
    await contentDetailsQuery.submitFeedback({
      contentId: details.id,
      description: feedback.comment,
      entityType: details.entityType,
      rating: feedback.rating,
    });

    setUserReview((currentReview) => ({
      id: currentReview?.id ?? details.id * -10,
      author: currentUserName?.trim() || "Você",
      rating: feedback.rating,
      relativeDate: "Agora",
      comment: feedback.comment,
      showStars: true,
    }));
    setActiveTab("reviews");
  };

  const completeActiveMediaItem = () => {
    if (!activeMediaItem?.entityId) return;
    void completeContentItem(
      activeMediaItem,
      activeProgressItemId || activeMediaItem.id
    );
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
    completeActiveMediaItem();
  };

  const handleStartPlayback = async () => {
    if (activeMediaItem?.externalUrl) {
      setSelectedEmbeddedModule(activeMediaItem);
      setSelectedPdfModule(null);
      setActionStatus("Use o botão para abrir o conteúdo externo.");
      return;
    }

    if (activeMediaItem?.contentType === "quiz" && activeMediaItem.sourceUrl) {
      setSelectedEmbeddedModule(activeMediaItem);
      setSelectedPdfModule(null);
      completeActiveMediaItem();
      setActionStatus("Conteúdo carregado.");
      return;
    }

    if (activeMediaItem?.contentType === "pdf" && activeMediaItem.pdf) {
      setSelectedPdfModule(activeMediaItem);
      setSelectedEmbeddedModule(null);
      completeActiveMediaItem();
      setActiveTab("about");
      setActionStatus(`${activeMediaItem.title} carregado.`);
      return;
    }

    if (activeMediaItem && activeMediaItem.contentType !== "video") {
      setSelectedEmbeddedModule(activeMediaItem);
      setSelectedPdfModule(null);
      if (!requiresExplicitCompletion(activeMediaItem)) {
        completeActiveMediaItem();
      }
      setActiveTab("about");
      setActionStatus(`${activeMediaItem.title} carregado.`);
      return;
    }

    if (!videoSource) {
      setActionStatus("Selecione um módulo disponível para iniciar.");
      return;
    }

    setHasPlaybackStarted(true);
    setIsTypeCarouselOpen(false);

    if (videoRef.current && videoSource) {
      setIsPlaying(true);
      setActionStatus("Reprodução iniciada.");
      try {
        await videoRef.current.play();
        setIsVideoLoading(false);
      } catch {
        setIsPlaying(false);
        setActionStatus("O navegador bloqueou o início do vídeo.");
      }
      return;
    }

    setIsPlaying(true);
    setActionStatus("Prévia da reprodução iniciada.");
  };

  const handleTogglePlayback = async () => {
    const video = videoRef.current;

    if (isPlaying) {
      video?.pause();
      setIsPlaying(false);
      setActionStatus("Reprodução pausada.");
      return;
    }

    if (videoTime >= videoDuration) {
      setVideoTime(0);
      if (video) video.currentTime = 0;
    }

    if (video && videoSource) {
      setIsPlaying(true);
      try {
        await video.play();
        setIsVideoLoading(false);
      } catch {
        setIsPlaying(false);
        setActionStatus("O navegador bloqueou a reprodução do vídeo.");
      }
      return;
    }

    setIsPlaying(true);
    setActionStatus("Reprodução retomada.");
  };

  const handleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }

      if (!coverRef.current?.requestFullscreen) {
        setActionStatus("Tela cheia não é compatível com este navegador.");
        return;
      }

      await coverRef.current.requestFullscreen();
    } catch {
      setActionStatus("Não foi possível alterar o modo de tela cheia.");
    }
  };

  const handleSeek = (nextTime: number) => {
    const safeTime = Math.min(videoDuration, Math.max(0, nextTime));
    setVideoTime(safeTime);
    try {
      if (videoRef.current) videoRef.current.currentTime = safeTime;
    } catch {
      setActionStatus("Aguarde o vídeo terminar de carregar para avançar.");
    }
  };

  const handleVolumeChange = (nextVolume: number) => {
    setVideoVolume(nextVolume);
    setIsVideoMuted(nextVolume === 0);

    if (videoRef.current) {
      videoRef.current.volume = nextVolume / 100;
      videoRef.current.muted = nextVolume === 0;
    }
  };

  const handlePlaybackRateChange = (nextRate: number) => {
    setPlaybackRate(nextRate);
    setIsPlaybackSettingsOpen(false);
    if (videoRef.current) videoRef.current.playbackRate = nextRate;
    setActionStatus(`Velocidade alterada para ${nextRate}x.`);
    window.setTimeout(() => settingsButtonRef.current?.focus(), 0);
  };

  const resetViewerForLoadedModule = () => {
    setIsPlaying(false);
    setHasPlaybackStarted(false);
    setVideoTime(0);
    setVideoDuration(videoDurationSeconds);
    setPlaybackRate(1);
    setIsPlaybackSettingsOpen(false);
    setIsTypeCarouselOpen(false);
    setSelectedPdfModule(null);
    setSelectedMediaItem(null);
    setSelectedEmbeddedModule(null);
    setIsModuleLoading(false);
    setIsVideoLoading(false);
    setShouldAutoPlayVideo(false);
    setAreVideoControlsVisible(true);
  };

  const handleProgressItemSelect = async (
    item: ContentProgressItem,
    sectionKind: ContentProgressSectionKind
  ) => {
    if (sectionKind === "evaluation") {
      if (!item.entityId) {
        setActionStatus("Avaliação indisponível no momento.");
        return false;
      }
      if (item.completed) {
        setActionStatus(
          "Essa avaliação já foi respondida anteriormente e não pode ser refeita."
        );
        return false;
      }
      if (!isEvaluationUnlocked) {
        setActionStatus(
          "Conclua todos os conteúdos antes de acessar a avaliação."
        );
        return false;
      }
      navigate(
        `/app/conteudo/detalhes/${details.sourceType}/${details.id}/avaliacao/${item.entityId}`,
        {
          state: {
            detailsTitle: details.title,
            from: `${location.pathname}${location.search}`,
            fromKey: location.key,
            returnState: navigationState,
          },
        }
      );
      return true;
    }

    if (!item.entityId) {
      setActionStatus("Módulo indisponível no momento.");
      return false;
    }

    if (details.entityType === "conteudo") {
      navigate(`/app/conteudo/detalhes/conteudos/${item.entityId}`, {
        state: {
          from: originRoute ?? details.moduleRoute,
          fromKey: originLocationKey,
        },
      });
      cancelModuleScrollRef.current?.();
      cancelModuleScrollRef.current = scrollPageToTop();
      return false;
    }

    setActiveTab("about");
    const shouldScrollToTop = activeProgressItemId !== item.id;
    const requestId = ++moduleRequestIdRef.current;
    setIsModuleLoading(true);

    try {
      const module = await loadModule(item.entityId);
      if (moduleRequestIdRef.current !== requestId) return false;

      resetViewerForLoadedModule();
      displayContentItem(module);
      setHasSharedItemError(false);
      if (module.contentType === "video" && module.sourceUrl) {
        setHasPlaybackStarted(true);
        setShouldAutoPlayVideo(true);
      }
      setActiveProgressItemId(item.id);
      setActiveTab("about");
      setActionStatus(`${module.title} carregado.`);
      if (shouldScrollToTop) {
        cancelModuleScrollRef.current?.();
        cancelModuleScrollRef.current = scrollPageToTop();
      }
      if (
        module.contentType !== "video" &&
        !requiresExplicitCompletion(module)
      ) {
        void completeContentItem(module, item.id);
      }
      return true;
    } catch {
      if (moduleRequestIdRef.current !== requestId) return false;
      setActionStatus("Não foi possível carregar este módulo. Tente novamente.");
      return false;
    } finally {
      if (moduleRequestIdRef.current === requestId) {
        setIsModuleLoading(false);
      }
    }
  };

  const reviews = userReview
    ? [userReview, ...details.reviews]
    : details.reviews;
  const hasRating = typeof details.rating === "number" && details.rating > 0;
  const feedbacksCount =
    details.reviewsCount + (userReview && !details.userRating ? 1 : 0);
  const modulesLabel =
    details.modules && details.modules > 0
      ? `${details.modules} ${details.modules === 1 ? "módulo" : "módulos"}`
      : undefined;
  const hasMeta = Boolean(
    hasRating ||
      feedbacksCount > 0 ||
      details.duration ||
      modulesLabel ||
      details.publishedAt
  );
  const canStartPlayback = Boolean(isVideoContent && videoSource);
  const videoProgressText = `${formatVideoTime(videoTime)} de ${formatVideoTime(
    videoDuration
  )}`;
  const visibleVolume = isVideoMuted ? 0 : videoVolume;
  const showProgress = details.entityType !== "conteudo";

  return (
    <section className="content-page content-details-page" aria-labelledby="content-details-title">
      <div
        className="content-details-page__inner"
      >
        <div className="content-details-page__intro">
        <button
          type="button"
          className="content-details-page__back"
          onClick={() => navigateBack(details.moduleRoute)}
          aria-label="Voltar para a listagem"
        >
          <ArrowBackRoundedIcon aria-hidden="true" />
          <span>Voltar</span>
        </button>

        <header className="content-details-page__header">
          <h1 id="content-details-title">{details.title}</h1>

          {hasMeta && <div className="content-details-page__meta">
            {hasRating && (
              <span className="content-details-page__rating">
                <span>{details.rating?.toFixed(1)}</span>
                <StarRoundedIcon className="content-details-page__star" aria-hidden="true" />
                {feedbacksCount > 0 && <span>({feedbacksCount})</span>}
              </span>
            )}
            {hasRating && details.duration && (
              <span className="content-details-page__divider" aria-hidden="true" />
            )}
            {details.duration && <span>{details.duration}</span>}
            {(hasRating || details.duration) && modulesLabel && (
              <span className="content-details-page__divider" aria-hidden="true" />
            )}
            {modulesLabel && <span>{modulesLabel}</span>}
            {details.publishedAt && (
              <time dateTime={details.publishedAtIso}>
                Data: {details.publishedAt}
              </time>
            )}
          </div>}
        </header>
        </div>

        <div className="content-details-page__main">

        {hasSharedItemError && (
          <div className="content-details-page__error" role="alert">
            <h2>Item compartilhado indisponível</h2>
            <p>
              Não foi possível abrir o item compartilhado. Ele pode ter sido
              removido ou não estar disponível para o seu perfil. Selecione
              outro conteúdo na lista para continuar.
            </p>
          </div>
        )}

        {isModuleLoading && !hasDisplayedContent && <ContentModuleViewerSkeleton />}

        {isVideoContent && displayedContentItem && !hasSharedItemError && !selectedPdfModule && !selectedEmbeddedModule && <div
          ref={coverRef}
          className={`content-details-cover${
            videoSource
              ? " content-details-cover--video"
              : ""
          }${
            isTypeCarouselOpen
              ? " content-details-cover--carousel-open"
              : ""
          }`}
          onClick={hasPlaybackStarted ? showVideoControls : undefined}
          onPointerMove={hasPlaybackStarted ? showVideoControls : undefined}
        >
          <span
            className="content-details-cover__background"
            aria-hidden="true"
            style={{
              backgroundImage: "none",
            }}
          />
          {videoSource && (
            <video
              ref={videoRef}
              className="content-details-cover__media"
              src={isHlsSource ? undefined : videoSource}
              preload="auto"
              playsInline
              aria-hidden="true"
              tabIndex={-1}
              onLoadStart={() => setIsVideoLoading(true)}
              onLoadedMetadata={(event) => {
                const loadedDuration = event.currentTarget.duration;
                if (Number.isFinite(loadedDuration) && loadedDuration > 0) {
                  setVideoDuration(loadedDuration);
                }
                event.currentTarget.volume = videoVolume / 100;
                event.currentTarget.muted = isVideoMuted;
                event.currentTarget.playbackRate = playbackRate;
              }}
              onCanPlay={() => setIsVideoLoading(false)}
              onPlaying={() => setIsVideoLoading(false)}
              onWaiting={() => setIsVideoLoading(true)}
              onError={() => setIsVideoLoading(false)}
              onTimeUpdate={(event) =>
                setVideoTime(event.currentTarget.currentTime)
              }
              onPlay={handleVideoPlay}
              onPause={() => setIsPlaying(false)}
              onEnded={() => {
                setVideoTime(videoDuration);
                setIsPlaying(false);
              }}
            />
          )}
          {videoSource && isVideoLoading && !isTypeCarouselOpen && (
            <MediaLoadingIndicator
              className="content-details-cover__loading"
              label="Carregando conteúdo"
            />
          )}
          {!hasPlaybackStarted && relatedCards.length > 0 && (
            <button
              type="button"
              className={`content-details-action content-details-cover__type${
                isTypeCarouselOpen
                  ? " content-details-cover__type--expanded"
                  : ""
              }`}
              onClick={() => {
                const nextOpenState = !isTypeCarouselOpen;
                setIsTypeCarouselOpen(nextOpenState);
                setIsPlaying(false);
                setActionStatus(
                  nextOpenState
                    ? `Carousel de ${details.moduleTitle} aberto.`
                    : `Carousel de ${details.moduleTitle} fechado.`
                );
              }}
              aria-label={`${
                isTypeCarouselOpen ? "Ocultar" : "Ver"
              } tipo do conteúdo: ${details.moduleTitle}`}
              aria-expanded={isTypeCarouselOpen}
              aria-controls="content-type-carousel"
            >
              <BusinessCenterRoundedIcon aria-hidden="true" />
              {isTypeCarouselOpen && <span>{details.moduleTitle}</span>}
            </button>
          )}

          {isTypeCarouselOpen ? (
            <div id="content-type-carousel">
              <ContentDetailsCarousel
                title={details.moduleTitle}
                cards={relatedCards}
                activeContentId={details.id}
                onRequestClose={() => {
                  setIsTypeCarouselOpen(false);
                  setActionStatus(
                    `Carousel de ${details.moduleTitle} fechado.`
                  );
                }}
                onCardSelect={(card) => {
                  if (card.id === details.id) {
                    setIsTypeCarouselOpen(false);
                    setActionStatus("Você já está visualizando este conteúdo.");
                    return;
                  }

                  navigate(`/app/conteudo/detalhes/${card.sourceType ?? "conteudos"}/${card.id}`, {
                    state: {
                      from: navigationState?.from ?? details.moduleRoute,
                      fromKey: navigationState?.fromKey,
                      initiallySaved: false,
                    },
                  });
                }}
              />
            </div>
          ) : !hasPlaybackStarted ? (
            <>
              <div className="content-details-cover__actions">
                <button
                  type="button"
                  className={`content-details-action${isLiked ? " content-details-action--active" : ""}`}
                  onClick={handleLike}
                  disabled={contentDetailsQuery.isActionPending}
                  aria-label={isLiked ? "Remover curtida" : "Curtir conteúdo"}
                  aria-pressed={isLiked}
                >
                  {isLiked ? (
                    <ThumbUpAltRoundedIcon aria-hidden="true" />
                  ) : (
                    <ThumbUpAltOutlinedIcon aria-hidden="true" />
                  )}
                </button>
                <button
                  type="button"
                  className="content-details-action"
                  onClick={handleShare}
                  disabled={!displayedContentItem?.entityId || hasSharedItemError}
                  aria-label="Compartilhar conteúdo"
                >
                  <SendRoundedIcon aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={`content-details-action${isSaved ? " content-details-action--active" : ""}`}
                  onClick={handleSave}
                  disabled={contentDetailsQuery.isActionPending}
                  aria-label={isSaved ? "Remover dos favoritos" : "Salvar nos favoritos"}
                  aria-pressed={isSaved}
                >
                  {isSaved ? (
                    <BookmarkRoundedIcon aria-hidden="true" />
                  ) : (
                    <BookmarkBorderRoundedIcon aria-hidden="true" />
                  )}
                </button>
              </div>

              {canStartPlayback && (
                <button
                  type="button"
                  className="content-details-cover__play"
                  onClick={handleStartPlayback}
                  aria-label={activeMediaItem?.externalUrl ? "Abrir conteúdo" : "Reproduzir conteúdo"}
                >
                  <PlayArrowRoundedIcon aria-hidden="true" />
                </button>
              )}
            </>
          ) : (
            <div
              className={`content-details-video-controls${
                areVideoControlsVisible && !isVideoLoading
                  ? ""
                  : " content-details-video-controls--hidden"
              }`}
              role="group"
              aria-label="Controles do vídeo"
              aria-hidden={!areVideoControlsVisible || isVideoLoading}
            >
              <div className="content-details-video-controls__bar">
                <button
                  type="button"
                  className="content-details-video-controls__button content-details-video-controls__skip"
                  onClick={() => handleSeek(videoTime - 5)}
                  aria-label="Voltar 5 segundos"
                >
                  <SkipPreviousRoundedIcon aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="content-details-video-controls__button"
                  onClick={handleTogglePlayback}
                  aria-label={isPlaying ? "Pausar vídeo" : "Retomar vídeo"}
                  aria-pressed={isPlaying}
                >
                  {isPlaying ? (
                    <PauseRoundedIcon aria-hidden="true" />
                  ) : (
                    <PlayArrowRoundedIcon aria-hidden="true" />
                  )}
                </button>
                <button
                  type="button"
                  className="content-details-video-controls__button content-details-video-controls__skip"
                  onClick={() => handleSeek(videoTime + 5)}
                  aria-label="Avançar 5 segundos"
                >
                  <SkipNextRoundedIcon aria-hidden="true" />
                </button>

                <time className="content-details-video-controls__time">
                  {formatVideoTime(videoTime)}
                </time>
                <VideoControlRange
                  className="content-details-video-controls__progress"
                  label="Progresso do vídeo"
                  max={videoDuration}
                  value={videoTime}
                  step={0.01}
                  valueText={videoProgressText}
                  onChange={handleSeek}
                />
                <time className="content-details-video-controls__time">
                  {formatVideoTime(videoDuration)}
                </time>

                <button
                  type="button"
                  className="content-details-video-controls__button"
                  onClick={() => {
                    const nextMutedState = !isVideoMuted;
                    setIsVideoMuted(nextMutedState);

                    if (!nextMutedState && videoVolume === 0) {
                      setVideoVolume(65);
                    }

                    if (videoRef.current) {
                      videoRef.current.muted = nextMutedState;
                      if (!nextMutedState && videoVolume === 0) {
                        videoRef.current.volume = 0.65;
                      }
                    }
                  }}
                  aria-label={isVideoMuted ? "Ativar som" : "Silenciar vídeo"}
                  aria-pressed={isVideoMuted}
                >
                  {isVideoMuted || videoVolume === 0 ? (
                    <VolumeOffRoundedIcon aria-hidden="true" />
                  ) : (
                    <VolumeUpRoundedIcon aria-hidden="true" />
                  )}
                </button>
                <VideoControlRange
                  className="content-details-video-controls__volume"
                  label="Volume"
                  max={100}
                  value={visibleVolume}
                  valueText={`${visibleVolume}%`}
                  onChange={handleVolumeChange}
                />
                <div className="content-details-video-controls__settings">
                  <button
                    ref={settingsButtonRef}
                    type="button"
                    className="content-details-video-controls__button"
                    onClick={() =>
                      setIsPlaybackSettingsOpen((currentlyOpen) => !currentlyOpen)
                    }
                    aria-label={`Velocidade de reprodução: ${playbackRate}x`}
                    aria-expanded={isPlaybackSettingsOpen}
                    aria-controls="video-playback-speed-menu"
                  >
                    <SettingsRoundedIcon aria-hidden="true" />
                  </button>

                  {isPlaybackSettingsOpen && (
                    <div
                      id="video-playback-speed-menu"
                      className="content-details-video-controls__speed-menu"
                      role="menu"
                      aria-label="Velocidade de reprodução"
                      onKeyDown={(event) => {
                        if (event.key !== "Escape") return;
                        setIsPlaybackSettingsOpen(false);
                        settingsButtonRef.current?.focus();
                      }}
                    >
                      <strong>Velocidade</strong>
                      <div className="content-details-video-controls__speed-options">
                        {playbackRates.map((rate) => (
                          <button
                            key={rate}
                            type="button"
                            role="menuitemradio"
                            aria-checked={playbackRate === rate}
                            className={
                              playbackRate === rate
                                ? "content-details-video-controls__speed-option content-details-video-controls__speed-option--active"
                                : "content-details-video-controls__speed-option"
                            }
                            onClick={() => handlePlaybackRateChange(rate)}
                          >
                            {rate}x
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="content-details-video-controls__button"
                  onClick={handleFullscreen}
                  aria-label={
                    isFullscreen ? "Sair da tela cheia" : "Abrir em tela cheia"
                  }
                >
                  {isFullscreen ? (
                    <FullscreenExitRoundedIcon aria-hidden="true" />
                  ) : (
                    <FullscreenRoundedIcon aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
          )}

          {!hasPlaybackStarted && (
            <button
              type="button"
              className="content-details-action content-details-cover__help"
              onClick={handleHelp}
              aria-label="Abrir ajuda"
            >
              <HelpRoundedIcon aria-hidden="true" />
            </button>
          )}
        </div>}

        <div
          ref={tabsRef}
          className="content-details-tabs"
          role="tablist"
          aria-label="Detalhes do conteúdo"
        >
          <button
            type="button"
            id="content-tab-about"
            className={`content-details-tabs__button${activeTab === "about" ? " content-details-tabs__button--active" : ""}`}
            role="tab"
            aria-selected={activeTab === "about"}
            aria-controls="content-panel-about"
            onClick={() => setActiveTab("about")}
          >
            Sobre
          </button>
          <button
            type="button"
            id="content-tab-reviews"
            className={`content-details-tabs__button${activeTab === "reviews" ? " content-details-tabs__button--active" : ""}`}
            role="tab"
            aria-selected={activeTab === "reviews"}
            aria-controls="content-panel-reviews"
            onClick={() => setActiveTab("reviews")}
          >
            Feedbacks
          </button>
        </div>

        {activeTab === "about" ? (
          <div
            id="content-panel-about"
            className="content-details-about"
            role="tabpanel"
            aria-labelledby="content-tab-about"
          >
            <div className="content-details-section-heading">
              <div>
                <h2>{details.aboutTitle}</h2>
                <button
                  type="button"
                  className={`content-details-inline-action${isLiked ? " content-details-inline-action--active" : ""}`}
                  onClick={handleLike}
                  disabled={contentDetailsQuery.isActionPending}
                  aria-label={isLiked ? "Remover curtida" : "Curtir conteúdo"}
                  aria-pressed={isLiked}
                >
                  {isLiked ? (
                    <ThumbUpAltRoundedIcon aria-hidden="true" />
                  ) : (
                    <ThumbUpAltOutlinedIcon aria-hidden="true" />
                  )}
                </button>
                <button
                  type="button"
                  className="content-details-inline-action"
                  onClick={handleHelp}
                  aria-label="Abrir ajuda"
                >
                  <HelpRoundedIcon aria-hidden="true" />
                </button>
              </div>

              <div>
                <button
                  type="button"
                  className="content-details-inline-action"
                  onClick={handleShare}
                  disabled={!displayedContentItem?.entityId || hasSharedItemError}
                  aria-label="Compartilhar conteúdo"
                >
                  <SendRoundedIcon aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={`content-details-inline-action${isSaved ? " content-details-inline-action--active" : ""}`}
                  onClick={handleSave}
                  disabled={contentDetailsQuery.isActionPending}
                  aria-label={isSaved ? "Remover dos favoritos" : "Salvar nos favoritos"}
                  aria-pressed={isSaved}
                >
                  {isSaved ? (
                    <BookmarkRoundedIcon aria-hidden="true" />
                  ) : (
                    <BookmarkBorderRoundedIcon aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            <div className="content-details-about__description">
              {details.description.length > 0 ? (
                details.description.map((paragraph, index) => (
                  <p key={`${index}-${paragraph}`}>{paragraph}</p>
                ))
              ) : (
                <p>Descrição não informada.</p>
              )}
            </div>

            {selectedPdfModule?.pdf && (
              <PdfModuleViewer
                key={selectedPdfModule.id}
                title={selectedPdfModule.title}
                pdf={selectedPdfModule.pdf}
              />
            )}

            {selectedEmbeddedModule && (
              <EmbeddedContentViewer
                key={selectedEmbeddedModule.id}
                item={selectedEmbeddedModule}
                onExternalOpen={() =>
                  completeContentItem(
                    selectedEmbeddedModule,
                    activeProgressItemId || selectedEmbeddedModule.id
                  )
                }
                onScormComplete={() =>
                  completeContentItem(
                    selectedEmbeddedModule,
                    activeProgressItemId || selectedEmbeddedModule.id
                  )
                }
              />
            )}
          </div>
        ) : (
          <div
            id="content-panel-reviews"
            className="content-details-reviews"
            role="tabpanel"
            aria-labelledby="content-tab-reviews"
          >
            <h2>Feedbacks</h2>

            <div className="content-details-reviews__list">
              {reviews.length === 0 && (
                <p className="content-details-reviews__empty">
                  Este conteúdo ainda não possui feedbacks.
                </p>
              )}
              {reviews.map((review) => (
                <article className="content-review" key={review.id}>
                  <div className="content-review__header">
                    {review.authorPhotoUrl ? (
                      <img
                        className="content-review__avatar content-review__avatar--photo"
                        src={review.authorPhotoUrl}
                        alt={`Foto de ${review.author}`}
                      />
                    ) : (
                      <UserAvatarIcon className="content-review__avatar" />
                    )}
                    <div className="content-review__author">
                      <h3>{review.author}</h3>
                      <div className="content-review__rating-row">
                        <span>{review.rating.toFixed(1)}</span>
                        {review.showStars && (
                          <span
                            className="content-review__stars"
                            aria-label={`${review.rating} de 5 estrelas`}
                          >
                            {Array.from({ length: 5 }, (_, index) => (
                              <StarRoundedIcon
                                key={index}
                                className={
                                  index < Math.round(review.rating)
                                    ? "is-filled"
                                    : ""
                                }
                                aria-hidden="true"
                              />
                            ))}
                          </span>
                        )}
                        <span
                          className="content-review__separator"
                          aria-hidden="true"
                        />
                        <span className="content-review__date">
                          {review.relativeDate}
                        </span>
                      </div>
                    </div>
                  </div>
                  {review.comment && (
                    <p className="content-review__comment">{review.comment}</p>
                  )}
                </article>
              ))}
            </div>
          </div>
        )}

          {actionStatus && (
            <div
              className={`content-details-toast${
                isToastLeaving ? " content-details-toast--leaving" : ""
              }`}
              role="status"
              aria-live="polite"
            >
              <span>{actionStatus}</span>
              <button
                type="button"
                onClick={() => setIsToastLeaving(true)}
                aria-label="Fechar mensagem"
              >
                ×
              </button>
            </div>
          )}
        </div>

        <ContentProgressPanel
          key={`${details.sourceType}-${details.id}`}
          completedItemIds={completedProgressItemIds}
          progress={details.progress}
          feedbackSubjectLabel={feedbackSubjectLabels[details.sourceType]}
          initialFeedbackComment={details.userComment}
          initialRating={details.userRating}
          onFeedbackSubmit={handleFeedbackSubmit}
          onItemSelect={handleProgressItemSelect}
          onStatusChange={setActionStatus}
          selectedItemId={activeProgressItemId || undefined}
          showProgress={showProgress}
        />
      </div>
    </section>
  );
};

export default ContentDetailsPage;
